import { useEffect, useState } from 'react';
import { Header } from './components/Header';
import { DataUpload } from './components/DataUpload';
import { ErrorDisplay } from './components/ErrorDisplay';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ForecastChart } from './components/ForecastChart';
import { DiagnosticsPanel } from './components/DiagnosticsPanel';
import { ResidualPlot } from './components/ResidualPlot';
import { ExportResults } from './components/ExportResults';
import { StationarityTests } from './components/StationarityTests';
import { OnboardingWizard } from './components/OnboardingWizard';
import { ConfigureForecast } from './components/ConfigureForecast';
import { BrowserCompatibilityWarning } from './components/BrowserCompatibilityWarning';
import { AnalyticsConsent } from './components/AnalyticsConsent';
import { DataReview } from './components/DataReview';
import { NetlifyFeedbackForm } from './components/NetlifyFeedbackForm';
import { Toast } from './components/Toast';
import { ConfirmationModal } from './components/ConfirmationModal';
import { SimplifiedForecastResults } from './components/SimplifiedForecastResults';
import { useDataStore } from './stores/dataStore';
import { useUIStore } from './stores/uiStore';
import { useModelStore } from './stores/modelStore';
import { wasmEngine } from './lib/wasm';
import { analytics } from './utils/analytics';
import type { ACFPACFResult, StationarityTestResults} from './types/wasm';

function App() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAdvancedResults, setShowAdvancedResults] = useState(false);
  const rawData = useDataStore(state => state.rawData);
  const columnSelection = useDataStore(state => state.columnSelection);
  const setLoading = useUIStore(state => state.setLoading);
  const setError = useUIStore(state => state.setError);

  const config = useModelStore(state => state.config);
  const result = useModelStore(state => state.result);
  const setResult = useModelStore(state => state.setResult);
  const setIsRunning = useModelStore(state => state.setIsRunning);
  const isRunning = useModelStore(state => state.isRunning);

  const [forecastDates, setForecastDates] = useState<string[]>([]);
  const [acfPacfData, setAcfPacfData] = useState<ACFPACFResult | null>(null);
  const [stationarityData, setStationarityData] = useState<StationarityTestResults | null>(null);
  const [columnsConfirmed, setColumnsConfirmed] = useState(false);
  const [wasmToast, setWasmToast] = useState<{ show: boolean; message: string; type: 'info' | 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'info',
  });
  const [showClearDataConfirm, setShowClearDataConfirm] = useState(false); // Epic 7: Confirmation for clearing data
  const [showNewDatasetConfirm, setShowNewDatasetConfirm] = useState(false); // Epic 7: Confirmation for new dataset
  const [activeDiagTab, setActiveDiagTab] = useState<'performance' | 'residuals' | 'stationarity'>('performance');

  // Reset flow state when new data is uploaded
  useEffect(() => {
    console.log('[App] useEffect - rawData/columnSelection changed', {
      hasRawData: !!rawData,
      dateColumn: columnSelection.dateColumn,
      targetColumn: columnSelection.targetColumn,
      columnsConfirmed
    });

    if (rawData) {
      // Check if columns are already set from a loaded project
      if (columnSelection.dateColumn && columnSelection.targetColumn) {
        console.log('[App] Setting columnsConfirmed = true');
        setColumnsConfirmed(true);
      } else {
        console.log('[App] Setting columnsConfirmed = false');
        setColumnsConfirmed(false);
      }
    } else {
      setColumnsConfirmed(false);
    }
  }, [rawData, columnSelection.dateColumn, columnSelection.targetColumn]);

  // Initialize analytics
  useEffect(() => {
    const apiKey = import.meta.env.VITE_POSTHOG_API_KEY || 'phc_xjZY5ovd9GpNLfyADqR7C4bfe75xeJntRCUqa9EGjziw';
    const host = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

    console.log('[Analytics] Initializing with key:', apiKey ? 'present' : 'missing');

    if (apiKey) {
      analytics.init(apiKey, host);
    }
  }, []);

  // Load WASM module on mount (background loading with non-blocking toast)
  useEffect(() => {
    async function loadWasm() {
      const startTime = performance.now();
      try {
        // Show non-blocking toast instead of blocking modal
        setWasmToast({
          show: true,
          message: 'Loading forecasting engine...',
          type: 'info',
        });

        await wasmEngine.load();

        const loadTime = performance.now() - startTime;
        analytics.track('wasm_load_time', { duration_ms: loadTime });

        // Show success toast that auto-dismisses
        setWasmToast({
          show: true,
          message: '✓ Forecasting engine ready',
          type: 'success',
        });

        console.log('[App] WASM engine loaded successfully in', loadTime, 'ms');
      } catch (error) {
        setWasmToast({
          show: true,
          message: 'Failed to load forecasting engine',
          type: 'error',
        });
        setError({
          hasError: true,
          message: 'Failed to load forecasting engine',
          details: error instanceof Error ? error.message : String(error),
        });
        console.error('[App] WASM loading error:', error);
      }
    }

    loadWasm();
  }, [setError]);

  // Run forecast when user clicks the button
  const runForecast = async () => {
    if (!rawData || !columnSelection.dateColumn || !columnSelection.targetColumn) {
      setError({
        hasError: true,
        message: 'Missing data configuration',
        details: 'Please ensure date and target columns are selected',
      });
      return;
    }

    try {
      setIsRunning(true);
      setLoading({
        isLoading: true,
        message: 'Running forecast model...',
      });

      console.log('[App] Starting forecast with config:', {
        model_type: config.model_type,
        horizon: config.horizon,
        confidence_level: config.confidence_level,
        full_config: config
      });

      // DEBUG: Verify horizon is not 0
      if (config.horizon === 0 || !config.horizon) {
        console.error('[App] BUG DETECTED: config.horizon is 0 or undefined!', {
          horizon: config.horizon,
          config_snapshot: JSON.parse(JSON.stringify(config))
        });
      }

      // Track forecast run
      analytics.track('model_run', {
        model: config.model_type,
        horizon: config.horizon,
        data_points: rawData.rows.length,
      });

      // Extract timestamps and values from raw data
      const timestamps = rawData.rows.map((row: Record<string, unknown>) => {
        const dateValue = row[columnSelection.dateColumn!];
        return new Date(dateValue as string).getTime();
      });

      const values = rawData.rows.map((row: Record<string, unknown>) => {
        const value = row[columnSelection.targetColumn!];
        return typeof value === 'number' ? value : parseFloat(String(value));
      });

      console.log('[App] Data prepared:', { timestamps: timestamps.length, values: values.length });

      // Call WASM forecast function
      const resultJson = await wasmEngine.runForecast(
        timestamps,
        values,
        true, // is_unix_ms
        config
      );

      console.log('[App] Forecast result:', resultJson);

      // Generate forecast dates
      const lastDate = new Date(timestamps[timestamps.length - 1]);
      const dates: string[] = [];
      console.log('[App] Generating forecast dates:', {
        horizon: config.horizon,
        lastDate: lastDate.toISOString()
      });

      for (let i = 1; i <= config.horizon; i++) {
        const forecastDate = new Date(lastDate);
        forecastDate.setDate(forecastDate.getDate() + i);
        dates.push(forecastDate.toISOString().split('T')[0]);
      }

      console.log('[App] Generated forecast dates:', {
        count: dates.length,
        dates: dates,
        expected_horizon: config.horizon
      });

      setForecastDates(dates);

      // Calculate ACF/PACF and stationarity tests
      console.log('[App] Calculating ACF/PACF and stationarity tests');
      try {
        const acfPacfResult = await wasmEngine.calculateACFPACF(values);
        setAcfPacfData(acfPacfResult);
        // Store in window for PDF export
        (window as any).__waysightai_acfpacf_data = acfPacfResult;

        const stationarityResult = await wasmEngine.runStationarityTests(values);
        setStationarityData(stationarityResult);
        // Store in window for PDF export
        (window as any).__waysightai_stationarity_data = stationarityResult;
      } catch (error) {
        console.warn('[App] Could not calculate ACF/PACF or stationarity tests:', error);
        // Non-critical error, continue with forecast results
      }

      // Store result
      setResult(resultJson);
      setLoading({ isLoading: false });
      setIsRunning(false);

      // Scroll to top to show results (with small delay to ensure DOM is updated)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 150);

      console.log('[App] Forecast complete');
    } catch (error) {
      setIsRunning(false);
      setLoading({ isLoading: false });
      setError({
        hasError: true,
        message: 'Forecast failed',
        details: error instanceof Error ? error.message : String(error),
      });
      console.error('[App] Forecast error:', error);
    }
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('waysightai_onboarding_completed', 'true');
    setShowOnboarding(false);
    analytics.track('onboarding_completed');
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem('waysightai_onboarding_completed', 'true');
    setShowOnboarding(false);
    analytics.track('onboarding_skipped');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Onboarding wizard for first-time users */}
      {showOnboarding && (
        <OnboardingWizard
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Epic 6 components */}
      <BrowserCompatibilityWarning />
      <AnalyticsConsent />

      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Welcome section */}
          {!rawData && (
            <div className="max-w-3xl mx-auto text-center py-10 px-8 bg-gradient-to-b from-white to-slate-50/40 rounded-2xl border border-gray-200/80 shadow-sm mb-2">
              <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-4">
                Welcome to WaySight<span className="text-blue-600">AI</span>
              </h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
                Build fast, privacy-first time series forecasts. All processing happens 
                locally in your browser using high-performance WebAssembly (WASM)—your data 
                never uploads to any server.
              </p>
              <div className="flex flex-col items-center justify-center gap-3 text-sm sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    analytics.track('quick_tour_opened');
                    setShowOnboarding(true);
                  }}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 shadow-sm hover:shadow transition-all duration-200 cursor-pointer"
                >
                  🚀 Take a Quick Tour
                </button>
                <a
                  href="https://unpackingdata.com/post/edge-ai-forecasting-rust-wasm"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => analytics.track('article_clicked', { location: 'welcome' })}
                  className="px-5 py-2.5 border border-gray-250 bg-white hover:bg-slate-50 text-gray-650 rounded-xl text-sm font-semibold transition-all duration-200"
                >
                  Read the Rust/WASM Write-Up
                </a>
              </div>
            </div>
          )}

          {/* Data upload section */}
          {!rawData && <DataUpload />}

          {/* Supported Methods & Diagnostics overview (First page SEO / Feature list) */}
          {!rawData && (
            <div className="max-w-3xl mx-auto mt-2 bg-white rounded-2xl border border-gray-200/80 shadow-sm p-8">
              <h3 className="text-center font-extrabold text-gray-900 text-base mb-6">
                ⚡ Privacy-First Edge Forecasting Engine Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2 text-center md:text-left">
                  <div className="mx-auto md:mx-0 w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center text-lg mb-2">
                    🤖
                  </div>
                  <h4 className="font-bold text-sm text-gray-900">Supported Methods</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Auto-tuned <strong>ARIMA</strong> for trended data and <strong>Exponential Smoothing (ETS)</strong> for double/triple Holt-Winters seasonal patterns.
                  </p>
                </div>

                <div className="space-y-2 text-center md:text-left">
                  <div className="mx-auto md:mx-0 w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center text-lg mb-2">
                    📈
                  </div>
                  <h4 className="font-bold text-sm text-gray-900">Diagnostic Checks</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Local stationarity check using <strong>ADF & KPSS tests</strong>, plus distribution/density spread and <strong>ACF/PACF plots</strong> for residual patterns.
                  </p>
                </div>

                <div className="space-y-2 text-center md:text-left">
                  <div className="mx-auto md:mx-0 w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center text-lg mb-2">
                    🏆
                  </div>
                  <h4 className="font-bold text-sm text-gray-900">Accuracy Metrics</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Direct calculation of forecast errors including <strong>MAPE</strong>, <strong>RMSE</strong>, and <strong>MAE</strong> alongside complexity criteria <strong>AIC & BIC</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Data Review - Unified column selection and quality analysis */}
          {rawData && !columnsConfirmed && (
            <DataReview
              onConfirm={() => {
                console.log('[App] DataReview confirmed');
                setColumnsConfirmed(true);
              }}
            />
          )}

          {/* Step 2: Configure Forecast - browser-local analysis and forecasting */}
          {rawData && columnsConfirmed && !result && (
            <ConfigureForecast
              onRunForecast={() => {
                console.log('[App] Running forecast from ConfigureForecast');
                runForecast();
              }}
              onBack={() => {
                console.log('[App] Back to column selection');
                setColumnsConfirmed(false);
              }}
            />
          )}


          {/* Forecast Results */}
          {rawData && result && columnSelection.dateColumn && columnSelection.targetColumn && (
            <>
              {/* Chart */}
              <ForecastChart
                historicalData={rawData.rows.map((row: Record<string, unknown>) => {
                  const value = row[columnSelection.targetColumn!];
                  const numValue = typeof value === 'number' ? value : parseFloat(String(value));
                  return {
                    date: new Date(row[columnSelection.dateColumn!] as string).toISOString().split('T')[0],
                    value: numValue,
                  };
                })}
                forecastResult={result}
                forecastDates={forecastDates}
              />

              {/* Mode Switcher Segmented Control */}
              <div className="flex justify-center my-6">
                <div className="bg-gray-105 p-1 rounded-xl border border-gray-250/70 flex space-x-1 shadow-sm">
                  <button
                    onClick={() => {
                      setShowAdvancedResults(false);
                      analytics.track('view_simplified_results');
                    }}
                    className={`px-5 py-2.5 rounded-lg text-xs font-bold flex items-center space-x-2 transition-all duration-200 cursor-pointer ${
                      !showAdvancedResults
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <span>📊</span>
                    <span>Simplified Summary Report</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowAdvancedResults(true);
                      analytics.track('view_advanced_results');
                    }}
                    className={`px-5 py-2.5 rounded-lg text-xs font-bold flex items-center space-x-2 transition-all duration-200 cursor-pointer ${
                      showAdvancedResults
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <span>⚙️</span>
                    <span>Advanced Diagnostics Dashboard</span>
                  </button>
                </div>
              </div>

              {/* Simplified Results (Default View) */}
              {!showAdvancedResults && (
                <SimplifiedForecastResults
                  forecastResult={result}
                  forecastDates={forecastDates}
                  historicalData={rawData.rows.map((row: Record<string, unknown>) => {
                    const value = row[columnSelection.targetColumn!];
                    const numValue = typeof value === 'number' ? value : parseFloat(String(value));
                    return {
                      date: new Date(row[columnSelection.dateColumn!] as string).toISOString().split('T')[0],
                      value: numValue,
                    };
                  })}
                  modelType={config.model_type}
                />
              )}

              {/* Advanced Diagnostics (Collapsed by default) */}
              {showAdvancedResults && (
                <div className="space-y-6 animate-fadeIn">
                  {/* Info Card */}
                  <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-sm">
                    <h3 className="font-bold text-gray-905 text-sm">Advanced Diagnostics Dashboard</h3>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                      Detailed mathematical diagnostics, error metrics, residual distributions, and stationarity statistics computed locally.
                    </p>
                  </div>

                  {/* Tabs selector */}
                  <div className="flex bg-gray-105 p-0.5 rounded-lg border border-gray-200 w-fit">
                    <button
                      onClick={() => setActiveDiagTab('performance')}
                      className={`px-4 py-2 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                        activeDiagTab === 'performance'
                          ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                          : 'text-gray-500 hover:text-gray-950'
                      }`}
                    >
                      📈 Model Performance
                    </button>
                    <button
                      onClick={() => setActiveDiagTab('residuals')}
                      className={`px-4 py-2 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                        activeDiagTab === 'residuals'
                          ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                          : 'text-gray-500 hover:text-gray-950'
                      }`}
                    >
                      🎯 Residual Analysis
                    </button>
                    {stationarityData && (
                      <button
                        onClick={() => setActiveDiagTab('stationarity')}
                        className={`px-4 py-2 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                          activeDiagTab === 'stationarity'
                            ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                            : 'text-gray-500 hover:text-gray-950'
                        }`}
                      >
                        ⚡ Stationarity Check
                      </button>
                    )}
                  </div>

                  {/* Tab content */}
                  <div>
                    {activeDiagTab === 'performance' && (
                      <DiagnosticsPanel diagnostics={result.diagnostics} modelType={config.model_type} />
                    )}
                    {activeDiagTab === 'residuals' && (
                      <ResidualPlot residuals={result.residuals} fittedValues={result.fitted_values} />
                    )}
                    {activeDiagTab === 'stationarity' && stationarityData && (
                      <StationarityTests testResults={stationarityData} defaultExpanded={true} />
                    )}
                  </div>
                </div>
              )}

              <ExportResults
                forecastResult={result}
                forecastDates={forecastDates}
                historicalData={rawData.rows.map((row: Record<string, unknown>) => {
                  const value = row[columnSelection.targetColumn!];
                  const numValue = typeof value === 'number' ? value : parseFloat(String(value));
                  return {
                    date: new Date(row[columnSelection.dateColumn!] as string).toISOString().split('T')[0],
                    value: numValue,
                  };
                })}
              />

              {/* Action button */}
              <div className="flex justify-center">
                <button
                  onClick={() => setShowNewDatasetConfirm(true)}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  New Dataset
                </button>
              </div>
            </>
          )}
        </div>
      </main>

      <NetlifyFeedbackForm />

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-gray-500 sm:flex-row sm:px-6 lg:px-8">
          <span>WaySightAI</span>
          <div className="flex items-center gap-4">
            <a
              href="https://unpackingdata.com/post/edge-ai-forecasting-rust-wasm"
              target="_blank"
              rel="noreferrer"
              onClick={() => analytics.track('article_clicked', { location: 'footer' })}
              className="transition-colors hover:text-gray-900"
            >
              Rust/WASM write-up
            </a>
            <a
              href="https://www.linkedin.com/in/sumandan/"
              target="_blank"
              rel="noreferrer"
              onClick={() => analytics.track('linkedin_clicked', { location: 'footer' })}
              className="transition-colors hover:text-gray-900"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>

      {/* Global UI components */}
      <LoadingSpinner />
      <ErrorDisplay />

      {/* Non-blocking WASM loading toast */}
      {wasmToast.show && (
        <Toast
          message={wasmToast.message}
          type={wasmToast.type}
          duration={wasmToast.type === 'success' ? 3000 : 0} // Auto-dismiss success, keep others
          onDismiss={() => setWasmToast({ ...wasmToast, show: false })}
        />
      )}

      {/* Epic 7: Confirmation Modals for Destructive Actions */}
      <ConfirmationModal
        isOpen={showClearDataConfirm}
        title="Clear Data?"
        message="This will remove all uploaded data and reset the application. Are you sure you want to continue?"
        confirmText="Clear Data"
        cancelText="Cancel"
        onConfirm={() => {
          useDataStore.getState().clearData();
          setShowClearDataConfirm(false);
        }}
        onCancel={() => setShowClearDataConfirm(false)}
        showDontAskAgain={true}
        storageKey="waysightai_skip_clear_data_confirm"
      />

      <ConfirmationModal
        isOpen={showNewDatasetConfirm}
        title="Start New Dataset?"
        message="This will clear all current data and forecast results. Any unsaved work will be lost. Are you sure?"
        confirmText="Start New"
        cancelText="Cancel"
        onConfirm={() => {
          useDataStore.getState().clearData();
          setResult(null);
          setForecastDates([]);
          setAcfPacfData(null);
          setStationarityData(null);
          setShowNewDatasetConfirm(false);
        }}
        onCancel={() => setShowNewDatasetConfirm(false)}
        showDontAskAgain={true}
        storageKey="waysightai_skip_new_dataset_confirm"
      />
    </div>
  );
}

export default App;
